import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adApi } from '../../api/ad.api';
import { AdminAdListItem, AdType, AdPosition, AdScheduleStatus } from '../../types/Ad.api.type';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Megaphone,
  Plus,
  Pencil,
  Trash2,
  Calendar,
  BarChart3,
  RefreshCw,
  Settings,
  History,
  DollarSign,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdFormModal from './AdFormModal';
import AdDetailModal from './AdDetailModal';
import AdScheduleTimeline from './AdScheduleTimeline';
import { AdPerformanceDashboard } from './AdPerformanceDashboard';
import AdRotationStatus from './AdRotationStatus';
import TossAdConfig from './TossAdConfig';
import TossRewardAdConfig from './TossRewardAdConfig';
import { AdWatchHistory } from './AdWatchHistory';
import { AdEcpmManagement } from './AdEcpmManagement';

/** 위치 한국어 매핑 */
const POSITION_LABELS: Record<AdPosition, string> = {
  HOME_TOP: '홈 상단',
  HOME_MIDDLE: '홈 중단',
  MAIN_FEED_TOP: '메인 상단',
  MAIN_FEED: '메인 피드',
  MAIN_FEED_IMAGE: '메인 피드 이미지',
  POPULAR_FEED_TOP: '인기설문 상단',
  POPULAR_FEED: '인기설문',
  MY_FEED_TOP: '내설문 상단',
  MY_FEED: '내설문',
  DETAIL_COMMENT: '상세 댓글',
  ATTENDANCE: '출석 달력 하단',
  BENEFIT: '혜택 페이지',
  ATTENDANCE_RESULT: '출석 완료',
  EXCHANGE_RESULT: '환전 완료',
  WEEKLY_MISSION_RESULT: '미션 완료',
};

/** 상태 색상 매핑 */
const STATUS_COLORS: Record<AdScheduleStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-700',
  RESERVED: 'bg-yellow-100 text-yellow-700',
  COMPLETED: 'bg-gray-100 text-gray-500',
};

/** 상태 한국어 매핑 */
const STATUS_LABELS: Record<AdScheduleStatus, string> = {
  ACTIVE: '진행중',
  RESERVED: '예약',
  COMPLETED: '완료',
};

export const AdManagement = () => {
  /** States */
  const [filterType, setFilterType] = useState<AdType | undefined>(undefined);
  const [filterPosition, setFilterPosition] = useState<AdPosition | undefined>(undefined);
  const [filterActive, setFilterActive] = useState<boolean | undefined>(undefined);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAdId, setEditingAdId] = useState<number | null>(null);
  const [detailAdId, setDetailAdId] = useState<number | null>(null);
  const [detailAdTitle, setDetailAdTitle] = useState('');
  const [activeSubTab, setActiveSubTab] = useState<
    'list' | 'schedule' | 'performance' | 'rotation' | 'tossAd' | 'tossRewardAd' | 'watchHistory' | 'ecpm'
  >('list');

  const queryClient = useQueryClient();

  /** APIs */
  const { data: adList, isLoading } = useQuery({
    queryKey: ['adminAds', filterType, filterPosition, filterActive],
    queryFn: () => adApi.getAdList(filterType, filterPosition, filterActive).then((res) => res.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => adApi.deleteAd(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminAds'] });
      toast.success('광고가 삭제되었습니다.');
    },
  });

  /** Handlers */
  const handleCreate = () => {
    setEditingAdId(null);
    setIsFormOpen(true);
  };

  const handleEdit = (id: number) => {
    setEditingAdId(id);
    setIsFormOpen(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('이 광고를 삭제하시겠습니까?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingAdId(null);
    queryClient.invalidateQueries({ queryKey: ['adminAds'] });
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
          <Megaphone className="w-5 h-5" />
          광고 관리
        </h2>
        <Button onClick={handleCreate} className="flex items-center gap-2 w-full sm:w-auto justify-center">
          <Plus className="w-4 h-4" />
          광고 등록
        </Button>
      </div>

      {/* 서브탭: 목록 / 예약현황 */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={activeSubTab === 'list' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('list')}
        >
          목록
        </Button>
        <Button
          variant={activeSubTab === 'schedule' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('schedule')}
          className="flex items-center gap-1"
        >
          <Calendar className="w-3 h-3" />
          예약 현황
        </Button>
        <Button
          variant={activeSubTab === 'performance' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('performance')}
          className="flex items-center gap-1"
        >
          <BarChart3 className="w-3 h-3" />
          성과 분석
        </Button>
        <Button
          variant={activeSubTab === 'rotation' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('rotation')}
          className="flex items-center gap-1"
        >
          <RefreshCw className="w-3 h-3" />
          로테이션 현황
        </Button>
        <Button
          variant={activeSubTab === 'tossAd' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('tossAd')}
          className="flex items-center gap-1"
        >
          <Settings className="w-3 h-3" />
          배너광고 설정
        </Button>
        <Button
          variant={activeSubTab === 'tossRewardAd' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('tossRewardAd')}
          className="flex items-center gap-1"
        >
          <Settings className="w-3 h-3" />
          리워드/전면광고 설정
        </Button>
        <Button
          variant={activeSubTab === 'watchHistory' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('watchHistory')}
          className="flex items-center gap-1"
        >
          <History className="w-3 h-3" />
          광고 히스토리
        </Button>
        <Button
          variant={activeSubTab === 'ecpm' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setActiveSubTab('ecpm')}
          className="flex items-center gap-1"
        >
          <DollarSign className="w-3 h-3" />
          eCPM 관리
        </Button>
      </div>

      {activeSubTab === 'schedule' ? (
        <AdScheduleTimeline />
      ) : activeSubTab === 'performance' ? (
        <AdPerformanceDashboard />
      ) : activeSubTab === 'rotation' ? (
        <AdRotationStatus />
      ) : activeSubTab === 'tossAd' ? (
        <TossAdConfig />
      ) : activeSubTab === 'tossRewardAd' ? (
        <TossRewardAdConfig />
      ) : activeSubTab === 'watchHistory' ? (
        <AdWatchHistory />
      ) : activeSubTab === 'ecpm' ? (
        <AdEcpmManagement />
      ) : (
        <>
          {/* 필터 */}
          <div className="flex flex-wrap gap-2">
            <select
              className="border rounded-md px-3 py-1.5 text-sm"
              value={filterType ?? ''}
              onChange={(e) => setFilterType(e.target.value ? (e.target.value as AdType) : undefined)}
            >
              <option value="">전체 타입</option>
              <option value="SURVEY">설문광고</option>
              <option value="EXTERNAL">외부광고</option>
              <option value="SHARELINK">쉐어링크</option>
            </select>
            <select
              className="border rounded-md px-3 py-1.5 text-sm"
              value={filterPosition ?? ''}
              onChange={(e) => setFilterPosition(e.target.value ? (e.target.value as AdPosition) : undefined)}
            >
              <option value="">전체 위치</option>
              {Object.entries(POSITION_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <select
              className="border rounded-md px-3 py-1.5 text-sm"
              value={filterActive === undefined ? '' : String(filterActive)}
              onChange={(e) => setFilterActive(e.target.value === '' ? undefined : e.target.value === 'true')}
            >
              <option value="">전체 상태</option>
              <option value="true">활성</option>
              <option value="false">비활성</option>
            </select>
          </div>

          {/* 데스크톱: 테이블 */}
          <Card className="overflow-hidden hidden md:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">번호</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">타입</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">제목</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">위치</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">상태</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">PV</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">클릭</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">CTR</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-600">관리</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {isLoading ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                          로딩 중...
                        </td>
                      </tr>
                    ) : adList?.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                          등록된 광고가 없습니다.
                        </td>
                      </tr>
                    ) : (
                      adList?.map((ad: AdminAdListItem) => {
                        const ctr = ad.totalPv > 0 ? ((ad.totalClicks / ad.totalPv) * 100).toFixed(1) : '0.0';
                        return (
                          <tr key={ad.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">{ad.id}</td>
                            <td className="px-4 py-3">
                              <Badge
                                variant="outline"
                                className={
                                  ad.adType === 'SURVEY'
                                    ? 'border-blue-300 text-blue-600'
                                    : 'border-orange-300 text-orange-600'
                                }
                              >
                                {ad.adType === 'SURVEY' ? '설문' : ad.adType === 'SHARELINK' ? '쉐어링크' : '외부'}
                              </Badge>
                            </td>
                            <td
                              className="px-4 py-3 font-medium max-w-[200px] truncate text-blue-600 hover:underline cursor-pointer"
                              onClick={() => {
                                setDetailAdId(ad.id);
                                setDetailAdTitle(ad.title);
                              }}
                            >
                              {ad.title}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {ad.positions?.map((pos) => (
                                  <Badge
                                    key={pos.id}
                                    variant="outline"
                                    className={`text-[10px] ${STATUS_COLORS[pos.status]}`}
                                  >
                                    {POSITION_LABELS[pos.position]} ({STATUS_LABELS[pos.status]})
                                  </Badge>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center gap-1 text-xs font-medium ${ad.isActive ? 'text-green-600' : 'text-red-500'}`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full ${ad.isActive ? 'bg-green-500' : 'bg-red-400'}`}
                                />
                                {ad.isActive ? '활성' : '비활성'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">{ad.totalPv.toLocaleString()}</td>
                            <td className="px-4 py-3 text-right">{ad.totalClicks.toLocaleString()}</td>
                            <td className="px-4 py-3 text-right">{ctr}%</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => handleEdit(ad.id)}
                                  className="p-1 hover:bg-gray-100 rounded"
                                  title="수정"
                                >
                                  <Pencil className="w-4 h-4 text-gray-500" />
                                </button>
                                <button
                                  onClick={() => handleDelete(ad.id)}
                                  className="p-1 hover:bg-gray-100 rounded"
                                  title="삭제"
                                >
                                  <Trash2 className="w-4 h-4 text-red-400" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* 모바일: 카드 리스트 */}
          <div className="space-y-3 md:hidden">
            {isLoading ? (
              <div className="py-8 text-center text-gray-500">로딩 중...</div>
            ) : adList?.length === 0 ? (
              <div className="py-8 text-center text-gray-500">등록된 광고가 없습니다.</div>
            ) : (
              adList?.map((ad: AdminAdListItem) => {
                const ctr = ad.totalPv > 0 ? ((ad.totalClicks / ad.totalPv) * 100).toFixed(1) : '0.0';
                return (
                  <Card key={ad.id}>
                    <div className="p-4 space-y-3">
                      {/* 상단: 타입 + 상태 + 관리 */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className={
                              ad.adType === 'SURVEY'
                                ? 'border-blue-300 text-blue-600'
                                : 'border-orange-300 text-orange-600'
                            }
                          >
                            {ad.adType === 'SURVEY' ? '설문' : ad.adType === 'SHARELINK' ? '쉐어링크' : '외부'}
                          </Badge>
                          <span
                            className={`inline-flex items-center gap-1 text-xs font-medium ${ad.isActive ? 'text-green-600' : 'text-red-500'}`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${ad.isActive ? 'bg-green-500' : 'bg-red-400'}`}
                            />
                            {ad.isActive ? '활성' : '비활성'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleEdit(ad.id)}
                            className="p-1.5 hover:bg-gray-100 rounded"
                            title="수정"
                          >
                            <Pencil className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => handleDelete(ad.id)}
                            className="p-1.5 hover:bg-gray-100 rounded"
                            title="삭제"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </div>
                      </div>
                      {/* 제목 */}
                      <p
                        className="font-medium text-blue-600 text-sm hover:underline cursor-pointer"
                        onClick={() => {
                          setDetailAdId(ad.id);
                          setDetailAdTitle(ad.title);
                        }}
                      >
                        #{ad.id} {ad.title}
                      </p>
                      {/* 위치 뱃지 */}
                      <div className="flex flex-wrap gap-1">
                        {ad.positions?.map((pos) => (
                          <Badge key={pos.id} variant="outline" className={`text-[10px] ${STATUS_COLORS[pos.status]}`}>
                            {POSITION_LABELS[pos.position]} ({STATUS_LABELS[pos.status]})
                          </Badge>
                        ))}
                      </div>
                      {/* 성과 지표 */}
                      <div className="flex items-center gap-4 text-xs text-gray-500 pt-1 border-t">
                        <span>
                          PV <strong className="text-gray-700">{ad.totalPv.toLocaleString()}</strong>
                        </span>
                        <span>
                          클릭 <strong className="text-gray-700">{ad.totalClicks.toLocaleString()}</strong>
                        </span>
                        <span>
                          CTR <strong className="text-gray-700">{ctr}%</strong>
                        </span>
                      </div>
                    </div>
                  </Card>
                );
              })
            )}
          </div>
        </>
      )}

      {/* 등록/수정 모달 */}
      {isFormOpen && <AdFormModal adId={editingAdId} onClose={handleFormClose} />}

      {/* 광고 상세 성과 모달 */}
      {detailAdId !== null && (
        <AdDetailModal adId={detailAdId} adTitle={detailAdTitle} onClose={() => setDetailAdId(null)} />
      )}
    </div>
  );
};
